import React from 'react';

// Pro bg-effect: spotlight that follows cursor
const CursorSpotlight = ({ color = '#ffffff' }) => {
  const [pos, setPos] = React.useState({ x: -1000, y: -1000 });
  React.useEffect(() => {
    const handler = (e) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0" data-testid="bg-effect-spotlight" style={{
      background: `radial-gradient(600px circle at ${pos.x}px ${pos.y}px, ${color}22, transparent 60%)`,
      transition: 'background 80ms linear',
    }} />
  );
};

export const EXTRA_BG_KEYFRAMES = `
@keyframes grainShift {
  0%, 100% { transform: translate(0,0); }
  10% { transform: translate(-2%, -2%); }
  20% { transform: translate(2%, 1%); }
  30% { transform: translate(-1%, 3%); }
  40% { transform: translate(3%, -1%); }
  50% { transform: translate(-3%, 2%); }
  60% { transform: translate(1%, -3%); }
  70% { transform: translate(2%, 2%); }
  80% { transform: translate(-2%, 1%); }
  90% { transform: translate(1%, -2%); }
}
@keyframes starDrift {
  from { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  to { transform: translateY(-30vh) translateX(10vw); opacity: 0; }
}
`;

/* ─── Custom wallpaper + Pro background effect overlays (fixed, z-0) ─── */
export const ProfileBackgroundEffects = ({ profile, ts }) => {
  return (
    <>
      {/* Pro custom wallpaper — fixed behind content */}
      {profile.is_pro && profile.custom_wallpaper_url && (() => {
        const wScale = typeof profile.custom_wallpaper_scale === 'number' ? profile.custom_wallpaper_scale : 100;
        const wMode = profile.custom_wallpaper_mode || 'cover';
        const wBgSize = wMode === 'tile'
          ? `${wScale}% auto`
          : (wScale === 100 ? 'cover' : `${wScale}% auto`);
        return (
          <div
            aria-hidden
            className="pointer-events-none fixed inset-0 z-0"
            data-testid="custom-wallpaper-bg"
            style={{
              backgroundImage: `url(${profile.custom_wallpaper_url})`,
              backgroundSize: wBgSize,
              backgroundRepeat: wMode === 'tile' ? 'repeat' : 'no-repeat',
              backgroundPosition: 'center',
              opacity: (typeof profile.custom_wallpaper_opacity === 'number' ? profile.custom_wallpaper_opacity : 100) / 100,
              filter: profile.custom_wallpaper_blur > 0 ? `blur(${profile.custom_wallpaper_blur}px)` : 'none',
            }}
          />
        );
      })()}
      {/* Pro background effect overlay (fixed, behind content) */}
      {profile.is_pro && profile.bg_effect === 'grain' && (
        <div aria-hidden className="pointer-events-none fixed inset-0 z-0 opacity-[0.06] mix-blend-overlay" style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          animation: 'grainShift 8s steps(8) infinite',
        }} data-testid="bg-effect-grain" />
      )}
      {profile.is_pro && profile.bg_effect === 'starfield' && (
        <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden" data-testid="bg-effect-starfield">
          {Array.from({ length: 60 }).map((_, i) => (
            <span key={i} className="absolute rounded-full" style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 71) % 100}%`,
              width: `${1 + (i % 3)}px`,
              height: `${1 + (i % 3)}px`,
              background: ts['--theme-text'],
              opacity: 0.3 + (i % 5) * 0.1,
              animation: `starDrift ${30 + (i % 10) * 3}s linear ${-(i * 0.5)}s infinite`,
            }} />
          ))}
        </div>
      )}
      {profile.is_pro && profile.bg_effect === 'spotlight' && (
        <CursorSpotlight color={ts['--theme-accent']} />
      )}
    </>
  );
};
